import React, { useState, useContext } from "react";
import { Modal, Button, Skeleton, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  ReadOutlined,
  HomeOutlined,
  ShareAltOutlined,
  ArrowRightOutlined,
  ThunderboltOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { AuthContext } from "../context/AuthProvider";
import { device } from "../lib/breakpoints";
import { trackEvent } from "../helpers/mixpanel";
import userModel from "../lib/firebase/userModel";
import unitModel from "../lib/firebase/unitModel";
import { hasLeoKnowledgeBaseContext } from "../helpers/opsChatSetup";

const { Text } = Typography;

// Inline color on icons does not always win over Ant/portal + SVG attrs; use same trick as hero bolt.
const ICON_IN_GREEN_CIRCLE = { fontSize: 18, filter: "brightness(0) invert(1)" };
const CHECK_ON_GREEN = { color: "#fff" };

// styled(Modal)’s className does not wrap the portaled modal tree; .ant-modal-close in the template
// never matches. rootClassName + createGlobalStyle + explicit closeIcon.
const LoOpsChatModalCloseColor = createGlobalStyle`
  .lo-opschat-firsttime .ant-modal-close,
  .lo-opschat-firsttime .ant-modal-close .anticon,
  .lo-opschat-firsttime .ant-modal-close-x {
    color: rgba(0, 0, 0, 0.88) !important;
  }
  .lo-opschat-firsttime .ant-modal-close:hover,
  .lo-opschat-firsttime .ant-modal-close:hover .anticon,
  .lo-opschat-firsttime .ant-modal-close-x:hover {
    color: #000 !important;
  }
`;

const OpsChatFirstTimeModal = ({ isOpen, onClose }) => {
  const { user, userInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [taskStatus, setTaskStatus] = useState({
    unit: false,
    knowledgeBase: false,
    chatLink: false,
  });

  React.useEffect(() => {
    if (!isOpen || !user) return;

    const checkTaskStatus = async () => {
      setTasksLoading(true);
      try {
        const units = await unitModel.getByOwner(user.uid);
        const hasKnowledgeBase = hasLeoKnowledgeBaseContext(userInfo, units);
        const hasUnit = units && units.length > 0;
        const hasChatLink = hasUnit;

        setTaskStatus({
          unit: hasUnit,
          knowledgeBase: hasKnowledgeBase,
          chatLink: hasChatLink,
        });
      } catch (error) {
        console.error("[OpsChatFirstTimeModal] Error checking task status:", error);
      } finally {
        setTasksLoading(false);
      }
    };

    checkTaskStatus();
  }, [isOpen, user, userInfo]);

  const handleDismiss = async () => {
    setLoading(true);
    try {
      trackEvent("opschat_first_time_modal_dismissed", { user_id: user?.uid });
      if (user?.uid) {
        await userModel.update({
          id: user.uid,
          data: { hasSeenOpsChatFirstTimeModal: true },
        });
      }
      onClose();
    } catch (error) {
      console.error("[OpsChatFirstTimeModal] Error dismissing:", error);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (taskKey) => {
    trackEvent("opschat_first_time_modal_task_clicked", {
      task: taskKey,
      user_id: user?.uid,
    });

    switch (taskKey) {
      case "knowledgeBase":
        navigate("/settings#knowledge-base");
        break;
      case "unit":
        navigate("/units");
        break;
      case "chatLink":
        navigate("/settings#tenant-chat");
        break;
      default:
        break;
    }

    onClose();
  };

  const tasks = [
    {
      key: "unit",
      icon: <HomeOutlined style={ICON_IN_GREEN_CIRCLE} />,
      title: "Add your first door",
      description:
        "Start on Doors: add a rental so you can attach a knowledge base and generate a private tenant chat link. No tenant app required. They open the link you send.",
      completed: taskStatus.unit,
      action: "Open Doors",
    },
    {
      key: "knowledgeBase",
      icon: <ReadOutlined style={ICON_IN_GREEN_CIRCLE} />,
      title: "Upload your lease & house rules",
      description:
        "Leo AI answers from your policies. Upload your lease and any house rules so every response is grounded in your actual agreements, not guesswork.",
      completed: taskStatus.knowledgeBase,
      action: "Open Knowledge Base",
    },
    {
      key: "chatLink",
      icon: <ShareAltOutlined style={ICON_IN_GREEN_CIRCLE} />,
      title: "Share your tenant chat link",
      description:
        "Copy the link from Settings → Tenant Chat and send it to your tenant by text or email. Leo starts handling their messages immediately.",
      completed: taskStatus.chatLink,
      action: "Go to Chat Settings",
    },
  ];

  const completedCount = tasks.filter((t) => t.completed).length;
  const totalTasks = tasks.length;
  const remainingTasks = totalTasks - completedCount;

  return (
    <>
      {isOpen ? <LoOpsChatModalCloseColor /> : null}
      <StyledModal
        open={isOpen}
        onCancel={handleDismiss}
        footer={null}
        closable
        width={600}
        centered
        maskClosable
        rootClassName="lo-opschat-firsttime"
        closeIcon={
          <CloseOutlined style={{ color: "rgba(0, 0, 0, 0.88)", fontSize: 16 }} />
        }
        styles={{
          content: {
            maxHeight: "min(92vh, calc(100dvh - 1.5rem))",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: 0,
          },
          body: {
            padding: 0,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          },
        }}
      >
      <ModalContent>
        <HeaderSection>
          <HeaderEyebrow>Leo AI · Tenant chat</HeaderEyebrow>
          <HeaderTitle>Get OpsChat up and running</HeaderTitle>
          <SubheaderText type="secondary">
            Three steps to give Leo AI everything it needs to handle your
            tenants&nbsp;24/7.
          </SubheaderText>
        </HeaderSection>

        <HeroSection>
          <HeroIcon>
            <ThunderboltOutlined style={{ filter: "brightness(0) invert(1)" }} />
          </HeroIcon>
          <HeroContent>
            <HeroText>
              <HeroTitle>Leo AI first responder</HeroTitle>
              <HeroSubtitle>
                Once set up, Leo handles routine questions immediately, surfaces
                real emergencies to you with context, and keeps a verified
                transcript of every conversation.
              </HeroSubtitle>
            </HeroText>
            <HeroBadge $isComplete={remainingTasks === 0}>
              {remainingTasks === 0
                ? "You're set up"
                : `${remainingTasks} step${remainingTasks === 1 ? "" : "s"} remaining`}
            </HeroBadge>
          </HeroContent>
        </HeroSection>

        {!tasksLoading && (
          <ProgressSection>
            <ProgressText>
              {completedCount} of {totalTasks} steps completed
            </ProgressText>
            <ProgressBar>
              <ProgressFill width={(completedCount / totalTasks) * 100} />
            </ProgressBar>
          </ProgressSection>
        )}

        <TasksList>
          {tasksLoading
            ? [1, 2, 3].map((index) => (
                <TaskSkeleton key={index}>
                  <TaskSkeletonLeft>
                    <Skeleton.Avatar active size={40} shape="circle" />
                    <TaskSkeletonContent>
                      <Skeleton
                        active
                        paragraph={{ rows: 2, width: ["100%", "80%"] }}
                        title={false}
                      />
                    </TaskSkeletonContent>
                  </TaskSkeletonLeft>
                  <Skeleton.Avatar active size={38} shape="circle" />
                </TaskSkeleton>
              ))
            : tasks.map((task) => (
                <TaskItem
                  type="button"
                  key={task.key}
                  completed={task.completed}
                  onClick={() => {
                    if (!task.completed) {
                      handleTaskClick(task.key);
                    }
                  }}
                  disabled={task.completed}
                >
                  <TaskLeft>
                    <TaskIcon completed={task.completed}>
                      {task.completed ? (
                        <CheckCircleOutlined style={CHECK_ON_GREEN} />
                      ) : (
                        task.icon
                      )}
                      {!task.completed && <TaskBadge />}
                    </TaskIcon>
                    <TaskContent>
                      <TaskHeader>
                        <TaskTitle completed={task.completed}>
                          {task.title}
                        </TaskTitle>
                        <TaskStatusChip $completed={task.completed}>
                          {task.completed ? "Complete" : "To do"}
                        </TaskStatusChip>
                      </TaskHeader>
                      <TaskDescription>{task.description}</TaskDescription>
                    </TaskContent>
                  </TaskLeft>

                  {!task.completed && (
                    <TaskActionIcon aria-hidden="true">
                      <ArrowRightOutlined />
                    </TaskActionIcon>
                  )}
                </TaskItem>
              ))}
        </TasksList>

        <FooterSection>
          <Button
            type="primary"
            onClick={handleDismiss}
            loading={loading}
            size="large"
          >
            Got it
          </Button>
        </FooterSection>
      </ModalContent>
    </StyledModal>
    </>
  );
};

export default OpsChatFirstTimeModal;

/* Styled to match FirstTimeUserModal (full OS): same layout, spacing, and tokens */

const StyledModal = styled(Modal)`
  z-index: 10000 !important;

  .ant-modal-mask {
    z-index: 9999 !important;
  }

  .ant-modal-wrap {
    z-index: 10000 !important;
  }

  .ant-modal {
    z-index: 10001 !important;
  }

  .ant-modal-content {
    border-radius: 16px;
    /* maxHeight + flex + body scroll: set via Modal styles prop; keep box shadow */
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);

    @media ${device.mobile} {
      border-radius: 16px 16px 0 0;
      margin: 0;
      max-width: 100vw;
    }
  }

  .ant-modal-header {
    display: none;
  }

  .ant-modal-body {
    padding: 0;
    /* min-height:0 + overflow from Modal styles prop so tall content scrolls inside viewport */
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
    z-index: 10;
    color: rgba(0, 0, 0, 0.88);

    .ant-modal-close-x,
    .anticon {
      color: rgba(0, 0, 0, 0.88);
    }

    &:hover,
    &:hover .ant-modal-close-x,
    &:hover .anticon {
      color: #000;
    }

    @media ${device.mobile} {
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
    }
  }

  @media ${device.mobile} {
    .ant-modal-wrap {
      /* align sheet to bottom on small screens so top margin + max-height stay on-screen */
      display: flex;
      align-items: flex-end;
      padding: 0;
    }

    .ant-modal {
      margin: 0 auto;
      max-width: 100vw;
      top: 0;
      padding-bottom: env(safe-area-inset-bottom, 0);
    }
  }
`;

const ModalContent = styled.div`
  padding: 2rem;
  /* Outer scroll: .ant-modal-body (set via Modal styles prop) */

  @media ${device.mobile} {
    padding: 1.25rem 1rem;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  @media ${device.mobile} {
    margin-bottom: 1.25rem;
    padding: 0 0.5rem;
  }
`;

const HeaderEyebrow = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-primary, #2c5f2d);
  font-family: var(--font-primary);
  text-align: center;
`;

const HeaderTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  color: var(--color-text);
  font-size: 1.75rem;
  line-height: 1.2;
  font-weight: 700;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.5rem;
    line-height: 1.3;
    margin-bottom: 0.375rem;
  }
`;

const SubheaderText = styled(Text)`
  display: block;
  font-size: 0.95rem;
  line-height: 1.45;
  text-align: center;
  color: var(--color-text-light);
  text-wrap: pretty;

  @media (min-width: 768px) {
    max-width: 32rem;
    margin-left: auto;
    margin-right: auto;
  }

  @media ${device.mobile} {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const ProgressSection = styled.div`
  margin-bottom: 2rem;

  @media ${device.mobile} {
    margin-bottom: 1.5rem;
  }
`;

const ProgressText = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-light);
  margin-bottom: 0.5rem;
  font-weight: 500;

  @media ${device.mobile} {
    font-size: 0.8rem;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background: rgba(15, 118, 110, 0.12);
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => Math.min(props.width, 100)}%;
  background: linear-gradient(90deg, #4bc069 0%, #2f855a 100%);
  border-radius: inherit;
  transition: width 0.4s ease;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -60%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(255, 255, 255, 0.8) 50%,
      transparent 100%
    );
    animation: progressShine 2.6s ease-in-out infinite;
    opacity: 0.8;
  }

  @keyframes progressShine {
    0% {
      transform: translateX(-100%);
      opacity: 0;
    }
    15% {
      opacity: 0.9;
    }
    50% {
      transform: translateX(180%);
      opacity: 0.6;
    }
    100% {
      transform: translateX(220%);
      opacity: 0;
    }
  }
`;

const HeroSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  margin-bottom: 1.75rem;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.08),
    rgba(44, 95, 45, 0.02)
  );
  border: 1px solid rgba(44, 95, 45, 0.1);

  @media ${device.mobile} {
    gap: 0.75rem;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const HeroIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: var(--color-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;

  @media ${device.mobile} {
    width: 44px;
    height: 44px;
    font-size: 1.25rem;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.625rem;
  }
`;

const HeroText = styled.div`
  flex: 1;
`;

const HeroTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: var(--color-text);
  margin-bottom: 0.25rem;

  @media ${device.mobile} {
    font-size: 0.95rem;
  }
`;

const HeroSubtitle = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.8rem;
    line-height: 1.35;
  }
`;

const HeroBadge = styled.div`
  background: ${(props) =>
    props.$isComplete ? "rgba(44,95,45,0.1)" : "rgba(244, 187, 67, 0.25)"};
  color: ${(props) => (props.$isComplete ? "var(--color-primary)" : "#9b5b00")};
  font-weight: 600;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
  flex-shrink: 0;
`;

const TasksList = styled.div`
  margin-bottom: 2rem;

  @media ${device.mobile} {
    margin-bottom: 1.5rem;
  }
`;

const TaskSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  border: 2px solid var(--color-border-light);
  border-radius: 12px;
  background: white;

  @media ${device.mobile} {
    padding: 1rem;
    margin-bottom: 0.625rem;
  }
`;

const TaskSkeletonLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;

  @media ${device.mobile} {
    gap: 0.75rem;
  }
`;

const TaskSkeletonContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TaskItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  border: 2px solid var(--color-border-light);
  border-radius: 12px;
  background: white;
  cursor: ${(props) => (props.completed ? "default" : "pointer")};
  transition: all 0.2s ease;
  text-align: left;
  outline: none;

  &:disabled {
    cursor: default;
    opacity: 1;
    background: white;
    color: inherit;
  }

  &:hover {
    ${(props) =>
      !props.completed &&
      `
      border-color: var(--color-primary);
      box-shadow: 0 6px 16px rgba(44, 95, 45, 0.12);
      transform: translateY(-2px);
    `}
  }

  @media ${device.mobile} {
    padding: 1rem;
    margin-bottom: 0.625rem;
    gap: 0.75rem;
    min-height: 80px;

    &:active {
      ${(props) =>
        !props.completed &&
        `
        transform: scale(0.98);
      `}
    }
  }
`;

const TaskLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
  min-width: 0;

  @media ${device.mobile} {
    gap: 0.75rem;
  }
`;

const TaskIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-right: 1rem;
  background: var(--color-primary);
  flex-shrink: 0;
  position: relative;

  .anticon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 100% !important;
  }

  .anticon svg {
    width: 1.25rem !important;
    height: 1.25rem !important;
  }

  @media ${device.mobile} {
    width: 36px;
    height: 36px;
    margin-right: 0.75rem;
    font-size: 1.1rem;

    .anticon svg {
      width: 1.1rem !important;
      height: 1.1rem !important;
    }
  }
`;

const TaskBadge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff4d4f;
  border: 2px solid white;
  z-index: 10;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
`;

const TaskContent = styled.div`
  flex: 1;
  min-width: 0;

  @media ${device.mobile} {
    flex: 1 1 auto;
  }
`;

const TaskHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    flex-wrap: nowrap;
    gap: 0.375rem;
    margin-bottom: 0.375rem;
  }
`;

const TaskTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${(props) =>
    props.completed ? "var(--color-primary)" : "var(--color-text)"};
  margin-bottom: 0;
  text-decoration: ${(props) => (props.completed ? "line-through" : "none")};
  opacity: ${(props) => (props.completed ? 0.7 : 1)};
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.3;
    width: 100%;
    min-width: 0;
    overflow-wrap: break-word;
    word-break: normal;
  }
`;

const TaskDescription = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-light);
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.8rem;
    line-height: 1.35;
  }
`;

const TaskStatusChip = styled(Tag)`
  border-radius: 999px;
  padding: 0 0.75rem;
  border-color: transparent !important;
  font-size: 0.75rem;
  background: ${(props) =>
    props.$completed ? "rgba(44, 95, 45, 0.15)" : "rgba(244, 187, 67, 0.25)"};
  color: ${(props) => (props.$completed ? "var(--color-primary)" : "#9b5b00")};
  flex-shrink: 0;
  height: 22px;
  line-height: 22px;
  margin: 0;

  @media ${device.mobile} {
    font-size: 0.7rem;
    padding: 0 0.625rem;
    height: 20px;
    line-height: 20px;
  }
`;

const TaskActionIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 2px solid var(--color-primary);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(44, 95, 45, 0.06);

  .anticon {
    font-size: 1rem;
  }

  @media ${device.mobile} {
    width: 32px;
    height: 32px;

    .anticon {
      font-size: 0.875rem;
    }
  }
`;

const FooterSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border-light);

  @media ${device.mobile} {
    padding-top: 1.25rem;

    .ant-btn {
      width: 100%;
      height: 48px;
      font-size: 1rem;
    }
  }
`;
